import { BehaviorSubject, of } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { AppConfig } from '../app.config';
import { EnvConfig } from '../env-config';
import { RxRequestService } from './rx-request.service';
import { TeamSettingsService } from './team-settings.service';
import { GlobalAction } from '../constants/constants';

export interface AllowedAction {
  type: string;
  display_name: string;
  description: string;
  is_disabled: boolean;
  disabled_reason: string;
}

const DummyAllowedActions: AllowedAction[] = [
  {
    type: 'CREATE_DESTINATION',
    display_name: 'Create Destination',
    description: 'Create a new destination for data output',
    is_disabled: false,
    disabled_reason: ''
  },
  {
    type: 'CREATE_PIPELINE',
    display_name: 'Create Pipeline',
    description: 'Create a new data processing pipeline',
    is_disabled: false,
    disabled_reason: ''
  },
  {
    type: 'CREATE_PROJECT',
    display_name: 'Create Transformation',
    description: 'Create a new transformation project',
    is_disabled: false,
    disabled_reason: ''
  }
];

export class AllowedActionsService {
  static deps = [AppConfig, RxRequestService, TeamSettingsService];

  private allowedActionsUrl: string;

  private allowedActionsSubject$ = new BehaviorSubject<AllowedAction[]>(null);

  private shouldBypassGlobalActionsCheck = !EnvConfig.isH20FeatureEnabled('RBAC');

  constructor(
    private appConfig: AppConfig,
    private rxRequestService: RxRequestService,
    private teamSettingsService: TeamSettingsService
  ) {
    this.allowedActionsUrl = this.appConfig.getAllowedActionsURL();
  }

  getAllowedActions(forceRefresh = false, handleUnauthorisedResponse = false) {
    if (!forceRefresh && this.allowedActionsSubject$.getValue()) {
      return this.allowedActionsSubject$.pipe(take(1));
    }

    const options = {
      uiOptions: {
        handleUnauthorisedResponse,
        showLoading: false,
        showErrorMsg: false
      }
    };

    if (this.shouldBypassGlobalActionsCheck) {
      this.allowedActionsSubject$.next(DummyAllowedActions);
      return of(DummyAllowedActions);
    }

    return this.rxRequestService.get(this.allowedActionsUrl, options).pipe(
      tap(res => {
        this.allowedActionsSubject$.next(res.data?.actions || []);
      })
    );
  }

  getAllowedActionsValue(): AllowedAction[] | null {
    return this.allowedActionsSubject$.getValue();
  }

  // Useful for manually updating the allowed actions, possibly from another part of the application.
  syncAllowedActions(allowedActions: AllowedAction[]) {
    this.allowedActionsSubject$.next(allowedActions);
  }

  getActionByType(type: GlobalAction): AllowedAction {
    // Team-settings RBAC off means the same thing it means to v1
    // (RbacService.canTakeAction): roles/permissions are ignored. The fetched
    // role-based action state must not gate the UI then — serve the
    // always-enabled defaults, exactly like the H2O-feature bypass does.
    // Checked at read time, not fetch time, so it can't race the parallel
    // team-settings resolve on dashboard load.
    if (!this.teamSettingsService.isRBACEnabled()) {
      return DummyAllowedActions.find(action => action.type === type);
    }

    const actions = this.allowedActionsSubject$.getValue() ?? [];
    return actions.find(action => action.type === type);
  }
}
